import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import HobbyArt from "./../../../assets/img/giftshop/landing/hobby-art.png";
import HobbyResultConfirmModal from "../../../modal/HobbyResultConfirmModal";
import {
  isAiSelfieCaricatureHobbyPostPaymentActive,
  shouldUseHobbyCaricatureFlowForStoredAiSelfieArt,
} from "../../../services/stylePromptResolver";
import {
  getPrimaryPreviewUrl,
  navigateToCreatedPersonHobby,
  type HobbyGenerationResult,
} from "../../../utils/hobbyGenerationNavigation";
import {
  isMultiPersonHobbySession,
  saveHobbyPersonResultAndContinue,
} from "../../../utils/hobbyPersonFlow";
import { useSelector } from "react-redux";
import type { RootState } from "../../../redux/store";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import SignOutConfirmModal from "../../../modal/SignOutConfirmModal";

const HobbyResultCompare = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showBackConfirmModal, setShowBackConfirmModal] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<{
    label: string;
    result: HobbyGenerationResult;
  } | null>(null);

  const { resultOne, resultTwo, stationData: stationDataFromNav, currentPersonIndex } = (location.state || {}) as {
    resultOne?: HobbyGenerationResult;
    resultTwo?: HobbyGenerationResult;
    stationData?: Record<string, unknown>;
    currentPersonIndex?: number;
  };

  const stationData = useMemo(() => {
    if (stationDataFromNav && typeof stationDataFromNav === "object") {
      return stationDataFromNav;
    }
    try {
      const selectedRaw = localStorage.getItem("selectedStation");
      const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
      const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
      return fromSelected || fromStorage;
    } catch {
      return {};
    }
  }, [stationDataFromNav]);

  const routeSlug = useMemo(() => {
    if (
      isAiSelfieCaricatureHobbyPostPaymentActive() ||
      shouldUseHobbyCaricatureFlowForStoredAiSelfieArt()
    ) {
      return "hobby-caricature";
    }
    const t = stationData?.type;
    return typeof t === "string" && t.trim() ? t : "hobby-caricature";
  }, [stationData?.type]);

  const { hobbyPeopleCount } = useSelector((s: RootState) => s.uploadFlow);
  const totalPeople = hobbyPeopleCount ?? 1;
  const currentPersonNumber =
    typeof currentPersonIndex === "number" ? currentPersonIndex + 1 : totalPeople;
  const isMultiPerson = isMultiPersonHobbySession();

  const results = useMemo(
    () =>
      [
        { label: "Result 1", result: resultOne },
        { label: "Result 2", result: resultTwo },
      ].filter((item) => item.result?.imageUrls?.length),
    [resultOne, resultTwo]
  );

  // No results to compare (reload/deep link) → don't strand the user;
  // send them back to regenerate.
  useEffect(() => {
    if (results.length === 0) {
      navigate(`/${routeSlug}/creating-person-hobby`, {
        replace: true,
        state: { stationData, currentPersonIndex },
      });
    }
  }, [results.length, navigate, routeSlug, stationData, currentPersonIndex]);

  const pendingPreviewUrl =
    getPrimaryPreviewUrl(pendingSelection?.result) || HobbyArt;

  const handleArtTap = (label: string, result: HobbyGenerationResult) => {
    setPendingSelection({ label, result });
    setConfirmOpen(true);
  };

  const handleConfirmNo = () => {
    setConfirmOpen(false);
    setPendingSelection(null);
  };

  const handleConfirmYes = () => {
    if (!pendingSelection?.result?.imageUrls?.length) return;
    const selectedUrl = getPrimaryPreviewUrl(pendingSelection.result);
    if (!selectedUrl) return;

    setConfirmOpen(false);

    if (isMultiPersonHobbySession()) {
      saveHobbyPersonResultAndContinue(navigate, routeSlug, selectedUrl, stationData);
      return;
    }

    navigateToCreatedPersonHobby(navigate, routeSlug, pendingSelection.result, stationData, {
      isFinalCombine: true,
    });
  };

  return (
    <>
      <StationThemeLayout
        className="hobbyResultReviewSection hobbyResultCompareSection p-0"
        station={stationData as { backgroundImageUrl?: string; type?: string; name?: string }}
        showStationLogo
        onBack={() => setShowBackConfirmModal(true)}
      >
        <div className="hobbyResultReviewInner mt-4">
          <div className="hobbyResultReviewContent">
            <h1 className="hobbyResultReviewHeading">
              {isMultiPerson
                ? `Select one result for person ${currentPersonNumber} of ${totalPeople}`
                : "Choose which one you like"}
            </h1>

            <div className="hobbyResultCompareGrid">
              <div className="hobbyResultCompareRow">
                {results.map(({ label, result }) => {
                  const previewUrl = getPrimaryPreviewUrl(result) || HobbyArt;
                  const isPending = pendingSelection?.result === result && confirmOpen;

                  return (
                    <button
                      key={label}
                      type="button"
                      className={`hobbyResultPreviewCard hobbyResultCompareCard${isPending ? " is-selected" : ""}`}
                      onClick={() => result && handleArtTap(label, result)}
                      aria-label={`Select ${label}`}
                    >
                      <img
                        src={previewUrl}
                        alt={label}
                        draggable={false}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </StationThemeLayout>

      {showBackConfirmModal && (
        <SignOutConfirmModal
          show={showBackConfirmModal}
          onHide={() => setShowBackConfirmModal(false)}
          onConfirm={() => {
            setShowBackConfirmModal(false);
            navigate("/");
          }}
          message="Are you sure you want to go back to the home screen?"
          cancelLabel="No"
          confirmLabel="Yes"
        />
      )}

      {confirmOpen && pendingSelection && (
        <HobbyResultConfirmModal
          show={confirmOpen}
          onHide={handleConfirmNo}
          selectionName={pendingSelection.label}
          imageUrl={pendingPreviewUrl}
          fallbackImage={HobbyArt}
          onConfirm={handleConfirmYes}
        />
      )}
    </>
  );
};

export default HobbyResultCompare;
